/**
 * AWS Client Factory
 * Provides Cognito Identity Pool credentials for AWS SDK services that require
 * direct SDK access (e.g. Transcribe Streaming for real-time audio).
 *
 * Most data access now goes through REST APIs — see services/*Api.ts files.
 * This module is only needed for:
 *   - getCredentialsProvider: Transcribe Streaming (real-time audio → text)
 *   - getUserIdentityId: Processing pipeline user identification
 */

import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { fetchAuthSession } from '@aws-amplify/auth';
import { createLogger } from './logger';

const log = createLogger('AWSClients');

// Configuration from environment
const region = import.meta.env.REACT_APP_AWS_REGION || 'eu-west-2';
const identityPoolId = import.meta.env.REACT_APP_IDENTITY_POOL_ID;
const userPoolId = import.meta.env.REACT_APP_USER_POOL_ID;

/**
 * Get AWS credentials provider using Cognito Identity Pool.
 * Exchanges the Cognito User Pool ID token for temporary AWS credentials.
 * Used by TranscribeStreamingService for real-time audio transcription.
 */
export async function getCredentialsProvider() {
  if (!identityPoolId || !userPoolId) {
    throw new Error('Identity Pool or User Pool not configured');
  }

  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();

  if (!idToken) {
    throw new Error('No authenticated session - user must be logged in');
  }

  log.debug('Creating credentials provider with Identity Pool');

  return fromCognitoIdentityPool({
    clientConfig: { region },
    identityPoolId,
    logins: {
      [`cognito-idp.${region}.amazonaws.com/${userPoolId}`]: idToken,
    },
  });
}

/**
 * Get the current user's Cognito Identity ID.
 * Used as the user identifier for the processing pipeline (jobs, documents).
 */
export async function getUserIdentityId(): Promise<string> {
  const session = await fetchAuthSession();
  const identityId = session.identityId;

  if (!identityId) {
    throw new Error('No identity ID available - user may not be authenticated');
  }

  return identityId;
}
